import type { ConversationDto } from '@restaurant/shared';
import { qk, useSuggestions } from '@restaurant/api-client/react';
import { useQueryClient } from '@tanstack/react-query';
import { Lock, Paperclip, SendHorizontal, Sparkles, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Spinner, Tooltip } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { TemplateSendDialog } from './TemplateSendDialog';
import { ImageSendDialog } from './ImageSendDialog';
import { windowState } from './format';
import { firstImage, formatBytes, prepareImage } from './imageAttachment';

/** Unsent text (and picture) per chat, so switching chats doesn't lose a half-typed reply. */
const drafts = new Map<string, { text: string; usedRunId?: string; file?: File }>();

export function Composer({
  conversation,
  incoming,
  onIncomingHandled,
}: {
  conversation: ConversationDto;
  /** A picture dropped on the chat; taken once and acknowledged through onIncomingHandled. */
  incoming?: File | null;
  onIncomingHandled?: () => void;
}) {
  const id = conversation.id;
  const [text, setTextState] = useState(() => drafts.get(id)?.text ?? '');
  const [usedRunId, setUsedRunIdState] = useState<string | undefined>(() => drafts.get(id)?.usedRunId);
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [file, setFileState] = useState<File | undefined>(() => drafts.get(id)?.file);
  const ref = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const win = windowState(conversation.windowOpenUntil);
  const { data: current } = useSuggestions(id, win.open);
  const suggestions = current ? { runId: current.runId, items: current.suggestions } : null;

  const setText = (t: string) => {
    setTextState(t);
    drafts.set(id, { ...drafts.get(id), text: t });
  };
  const setUsedRunId = (r: string | undefined) => {
    setUsedRunIdState(r);
    drafts.set(id, { ...drafts.get(id), text: drafts.get(id)?.text ?? '', usedRunId: r });
  };
  const setFile = (f: File | undefined) => {
    setFileState(f);
    drafts.set(id, { ...drafts.get(id), text: drafts.get(id)?.text ?? '', file: f });
  };
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : undefined), [file]);
  useEffect(() => () => void (preview && URL.revokeObjectURL(preview)), [preview]);

  /** Paste / drop / paperclip: make the picture sendable (convert or shrink if needed) and show it above the input. */
  const attach = async (f: File | undefined) => {
    if (!f || !win.open) return;
    try {
      setFile(await prepareImage(f));
      ref.current?.focus();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not use that picture');
    }
  };
  const setSuggestions = (convId: string, v: { runId: string; suggestions: string[] } | null) => qc.setQueryData(qk.suggestions(convId), v);

  useEffect(() => {
    setTextState(drafts.get(id)?.text ?? '');
    setUsedRunIdState(drafts.get(id)?.usedRunId);
    setFileState(drafts.get(id)?.file);
  }, [id]);

  useEffect(() => {
    if (!incoming) return;
    if (!win.open) toast.error('The 24-hour window is closed: pictures cannot be sent');
    else void attach(incoming);
    onIncomingHandled?.();
  }, [incoming]);

  const send = async () => {
    const t = text.trim();
    if ((!t && !file) || busy) return;
    setBusy(true);
    try {
      if (file) await api.conversations.sendImage(id, { file, filename: file.name, caption: t.slice(0, 1024) });
      else await api.conversations.reply(id, { text: t, ...(usedRunId ? { aiRunId: usedRunId } : {}) });
      drafts.delete(id);
      setTextState('');
      setUsedRunIdState(undefined);
      setFileState(undefined);
      setSuggestions(id, null);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
      ref.current?.focus();
    }
  };

  const suggest = async () => {
    const convId = id; // the result belongs to this chat even if staff switch chats meanwhile
    setSuggesting(true);
    try {
      const r = await api.conversations.suggestReplies(convId);
      setSuggestions(convId, r);
    } catch (err) {
      toastError(err);
    } finally {
      setSuggesting(false);
    }
  };

  const dismiss = () => {
    if (suggestions && !usedRunId) void api.aiRuns.reject(suggestions.runId).catch(() => undefined);
    setSuggestions(id, null);
  };

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const img = firstImage(e.clipboardData.files);
    if (!img) return; // plain text pastes as usual
    e.preventDefault();
    void attach(img);
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape' && file) {
      e.preventDefault();
      setFile(undefined);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void send();
    }
  };

  if (!win.open) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 border-t bg-card px-4 py-3">
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Lock className="size-4" /> The 24-hour window is closed. Only approved templates can be sent.
        </p>
        <TemplateSendDialog conversationId={id} trigger={<Button size="sm">Send a template</Button>} />
      </div>
    );
  }

  return (
    <div className="border-t bg-card">
      {suggestions?.items.length ? (
        <div className="flex items-start gap-2 border-b px-3 py-2">
          <Sparkles className="mt-1.5 size-4 shrink-0 text-primary" />
          <div className="flex flex-1 flex-wrap gap-2">
            {suggestions.items.map((s, i) => (
              <button
                key={i}
                onClick={() => {
                  setText(s);
                  setUsedRunId(suggestions.runId);
                  ref.current?.focus();
                }}
                className="max-w-full rounded-lg border bg-accent/50 px-2.5 py-1 text-left text-xs hover:bg-accent"
              >
                {s}
              </button>
            ))}
          </div>
          <button onClick={dismiss} className="rounded p-1 text-muted-foreground hover:bg-accent" aria-label="Dismiss suggestions">
            <X className="size-3.5" />
          </button>
        </div>
      ) : null}
      {file && preview ? (
        <div className="flex items-start gap-3 border-b bg-muted/40 px-4 py-3">
          <img src={preview} alt="Picture to send" className="max-h-48 max-w-[min(60%,20rem)] rounded-lg border bg-background object-contain" />
          <div className="min-w-0 flex-1 pt-1 text-xs text-muted-foreground">
            <p className="truncate font-medium text-foreground">{file.name}</p>
            <p>{formatBytes(file.size)} · type a caption below, then send</p>
          </div>
          <button onClick={() => setFile(undefined)} className="rounded-full p-1.5 text-muted-foreground hover:bg-accent" aria-label="Remove picture">
            <X className="size-4" />
          </button>
        </div>
      ) : null}
      <div className="flex items-end gap-2 p-3">
        <Tooltip content="Attach a picture (or paste / drop one)">
          <Button variant="ghost" size="icon" onClick={() => picker.current?.click()} aria-label="Attach a picture">
            <Paperclip />
          </Button>
        </Tooltip>
        <input
          ref={picker}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            void attach(firstImage(e.target.files));
            e.target.value = ''; // the same file can be picked again
          }}
        />
        <textarea
          ref={ref}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          onPaste={onPaste}
          rows={1}
          placeholder={file ? 'Add a caption…' : 'Type a message'}
          className="max-h-40 min-h-9 flex-1 resize-none rounded-2xl border border-input bg-background px-4 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          style={{ height: `${Math.min(160, 36 + (text.split('\n').length - 1) * 20)}px` }}
        />
        <Tooltip content="Suggest replies with AI">
          <Button variant="outline" size="icon" onClick={() => void suggest()} disabled={suggesting} aria-label="Suggest replies">
            {suggesting ? <Spinner /> : <Sparkles />}
          </Button>
        </Tooltip>
        <ImageSendDialog
          conversationId={id}
          caption={text}
          onSent={() => {
            drafts.delete(id);
            setTextState('');
            setUsedRunIdState(undefined);
            setFileState(undefined);
            setSuggestions(id, null);
          }}
        />
        <TemplateSendDialog conversationId={id} />
        <Button onClick={() => void send()} disabled={(!text.trim() && !file) || busy} className="rounded-full" size="icon" aria-label="Send">
          {busy ? <Spinner /> : <SendHorizontal />}
        </Button>
      </div>
    </div>
  );
}
